import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { inventoryService } from "services";
import { InventoryForm, InventoryQuery, InventoryUpdatePayload } from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type InventoryListKey = ["inventories"] | ["inventories", InventoryQuery];
type InventoryDetailKey = ["inventories", "detail", number | string];

const inventoryKeys = {
  all: ["inventories"] as const,
  list: (params?: InventoryQuery): InventoryListKey =>
    params ? ["inventories", params] : ["inventories"],
  detail: (id: number | string): InventoryDetailKey => [
    "inventories",
    "detail",
    id,
  ],
};

export function useInventory() {
  const queryClient = useQueryClient();

  const fetchList = (params?: InventoryQuery, enabled = true) => {
    return useQuery({
      queryKey: inventoryKeys.list(params),
      queryFn: () => inventoryService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: inventoryKeys.detail(id),
      queryFn: () => inventoryService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createInventory = useMutation({
    mutationFn: (payload: InventoryForm) => inventoryService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo tồn kho đầu kỳ thành công");
      }
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo tồn kho đầu kỳ thất bại");
      console.error("createInventory failed", err);
    },
  });

  const updateInventory = useMutation({
    mutationFn: (payload: InventoryUpdatePayload) =>
      inventoryService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Cập nhật tồn kho đầu kỳ thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật tồn kho đầu kỳ thất bại");
      console.error("updateInventory failed", err);
    },
  });

  const deleteInventory = useMutation({
    mutationFn: (id: number | string) => inventoryService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá tồn kho đầu kỳ thành công");
      }
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá tồn kho đầu kỳ thất bại");
      console.error("deleteInventory failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createInventory,
    updateInventory,
    deleteInventory,
  };
}
